import { useCallback, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'

interface HistogramData {
  bins: number[]
  maxCount: number
}

interface TimeMarker {
  time: Date
  label: string
  color?: string
}

interface TimeRangeSliderProps {
  minTime: Date
  maxTime: Date
  startTime: Date
  endTime: Date
  onChange: (start: Date, end: Date) => void
  disabled?: boolean
  histogram?: HistogramData | null // 事前計算されたヒストグラムデータ
  currentPlaybackTime?: Date | null // 現在の再生位置
  markers?: TimeMarker[] // 時間軸上に表示するマーカー
  onMarkerClick?: (time: Date) => void // マーカークリック時のコールバック
  snapMs?: number // 指定時、開始は floor、終了は ceil で外側に丸める（範囲拡張）
}

export default function TimeRangeSlider({
  minTime,
  maxTime,
  startTime,
  endTime,
  onChange,
  disabled = false,
  histogram,
  currentPlaybackTime,
  markers = [],
  onMarkerClick,
  snapMs,
}: TimeRangeSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'start' | 'end' | 'range' | null>(
    null
  )
  const [dragStartX, setDragStartX] = useState<number>(0)
  const [dragStartRange, setDragStartRange] = useState<{
    start: Date
    end: Date
  } | null>(null)

  // ドラッグ中のプレビュー値（ドロップ時にのみonChangeを呼ぶ）
  const [previewStart, setPreviewStart] = useState<Date | null>(null)
  const [previewEnd, setPreviewEnd] = useState<Date | null>(null)

  const totalMs = maxTime.getTime() - minTime.getTime()

  // ヒストグラムデータを表示用に変換
  const histogramDisplay =
    histogram && histogram.maxCount > 0
      ? histogram.bins.map((count) => ({
          count,
          height: (count / histogram.maxCount) * 100,
        }))
      : null

  const getPercent = (time: Date) => {
    if (totalMs === 0) return 0
    return ((time.getTime() - minTime.getTime()) / totalMs) * 100
  }

  // 表示用の値（ドラッグ中はプレビュー値、それ以外は実際の値）
  const displayStart = previewStart ?? startTime
  const displayEnd = previewEnd ?? endTime
  const startPercent = getPercent(displayStart)
  const endPercent = getPercent(displayEnd)

  // 再生位置のパーセンテージ
  const playbackPercent = currentPlaybackTime
    ? getPercent(currentPlaybackTime)
    : null

  const getTimeFromPercent = useCallback(
    (percent: number) => {
      const clampedPercent = Math.max(0, Math.min(100, percent))
      const ms = minTime.getTime() + (totalMs * clampedPercent) / 100
      return new Date(ms)
    },
    [minTime, totalMs]
  )

  // 30分など指定単位で外側に丸める（開始=floor、終了=ceil）
  const snapTime = useCallback(
    (date: Date, mode: 'floor' | 'ceil') => {
      if (!snapMs || snapMs <= 0) return date
      const ms = date.getTime()
      const snapped =
        mode === 'floor'
          ? Math.floor(ms / snapMs) * snapMs
          : Math.ceil(ms / snapMs) * snapMs
      // 全体境界からはみ出さないようクランプ
      const minMs = minTime.getTime()
      const maxMs = maxTime.getTime()
      return new Date(Math.max(minMs, Math.min(maxMs, snapped)))
    },
    [snapMs, minTime, maxTime]
  )

  const handlePointerDown =
    (handle: 'start' | 'end') => (e: React.PointerEvent) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setDragging(handle)
    }

  const handleRangePointerDown = (e: React.PointerEvent) => {
    if (disabled) return
    e.preventDefault()
    setDragging('range')
    setDragStartX(e.clientX)
    setDragStartRange({ start: startTime, end: endTime })
  }

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging || !sliderRef.current) return

      const rect = sliderRef.current.getBoundingClientRect()
      const percent = ((e.clientX - rect.left) / rect.width) * 100

      // 現在のプレビュー値または実際の値を基準にする
      const currentStart = previewStart ?? startTime
      const currentEnd = previewEnd ?? endTime

      if (dragging === 'start') {
        const raw = getTimeFromPercent(percent)
        const newStart = snapTime(raw, 'floor')
        if (newStart < currentEnd) {
          setPreviewStart(newStart)
        }
      } else if (dragging === 'end') {
        const raw = getTimeFromPercent(percent)
        const newEnd = snapTime(raw, 'ceil')
        if (newEnd > currentStart) {
          setPreviewEnd(newEnd)
        }
      } else if (dragging === 'range' && dragStartRange) {
        // 範囲全体をドラッグ（開始を floor で丸めて、duration を維持）
        const deltaX = e.clientX - dragStartX
        const deltaPercent = (deltaX / rect.width) * 100
        const deltaMs = (totalMs * deltaPercent) / 100

        const rangeDuration =
          dragStartRange.end.getTime() - dragStartRange.start.getTime()
        let newStartMs = dragStartRange.start.getTime() + deltaMs
        let newEndMs = dragStartRange.end.getTime() + deltaMs

        // 境界チェック
        if (newStartMs < minTime.getTime()) {
          newStartMs = minTime.getTime()
          newEndMs = newStartMs + rangeDuration
        }
        if (newEndMs > maxTime.getTime()) {
          newEndMs = maxTime.getTime()
          newStartMs = newEndMs - rangeDuration
        }

        if (snapMs && snapMs > 0) {
          const snappedStart = snapTime(new Date(newStartMs), 'floor')
          newStartMs = snappedStart.getTime()
          newEndMs = newStartMs + rangeDuration
        }

        setPreviewStart(new Date(newStartMs))
        setPreviewEnd(new Date(newEndMs))
      }
    },
    [
      dragging,
      startTime,
      endTime,
      previewStart,
      previewEnd,
      getTimeFromPercent,
      snapTime,
      snapMs,
      dragStartX,
      dragStartRange,
      totalMs,
      minTime,
      maxTime,
    ]
  )

  const handlePointerUp = useCallback(() => {
    // ドロップ時にonChangeを呼び出す
    if (dragging && (previewStart || previewEnd)) {
      const finalStart = previewStart ?? startTime
      const finalEnd = previewEnd ?? endTime
      onChange(finalStart, finalEnd)
    }

    setDragging(null)
    setDragStartRange(null)
    setPreviewStart(null)
    setPreviewEnd(null)
  }, [dragging, previewStart, previewEnd, startTime, endTime, onChange])

  useEffect(() => {
    if (dragging) {
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerUp)
      return () => {
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
        window.removeEventListener('pointercancel', handlePointerUp)
      }
    }
  }, [dragging, handlePointerMove, handlePointerUp])

  const formatTime = (date: Date) => {
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]
    return `${format(date, 'MM/dd')}(${dayOfWeek}) ${format(date, 'HH:mm')}`
  }

  return (
    <div
      className={`space-y-2 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      {/* ヒストグラム（データがある場合のみ表示） */}
      {histogramDisplay && histogramDisplay.length > 0 && (
        <div className="flex items-end h-12 gap-px mb-1">
          {histogramDisplay.map(
            (bin: { count: number; height: number }, i: number) => (
              <div
                key={i}
                className="flex-1 bg-blue-300 rounded-t-sm transition-all"
                style={{
                  height: `${bin.height}%`,
                  minHeight: bin.count > 0 ? '2px' : '0',
                }}
                title={`${bin.count} ポイント`}
              />
            )
          )}
        </div>
      )}

      {/* スライダー */}
      <div
        ref={sliderRef}
        className="relative h-6 bg-gray-200 rounded-full touch-none"
      >
        {/* 選択範囲（ドラッグ可能） */}
        <div
          className={`absolute h-full bg-primary-200 rounded-full ${
            dragging === 'range'
              ? 'cursor-grabbing bg-primary-300'
              : 'cursor-grab'
          } hover:bg-primary-300 transition-colors flex items-center justify-center`}
          style={{
            left: `${startPercent}%`,
            width: `${endPercent - startPercent}%`,
          }}
          onPointerDown={handleRangePointerDown}
        >
          {/* グリップインジケーター（範囲が十分広い場合のみ表示） */}
          {endPercent - startPercent > 10 && (
            <div className="flex gap-0.5 opacity-40">
              <div className="w-0.5 h-3 bg-primary-600 rounded-full" />
              <div className="w-0.5 h-3 bg-primary-600 rounded-full" />
              <div className="w-0.5 h-3 bg-primary-600 rounded-full" />
            </div>
          )}
        </div>

        {/* 開始ハンドル */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-primary-600 rounded-full shadow-md cursor-grab z-10 ${
            dragging === 'start' ? 'cursor-grabbing scale-110 z-20' : ''
          } hover:scale-110 transition-transform flex items-center justify-center`}
          style={{ left: `calc(${startPercent}% - 10px)` }}
          onPointerDown={handlePointerDown('start')}
        >
          <div className="w-1 h-3 bg-white rounded-full" />
        </div>

        {/* 終了ハンドル */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-primary-600 rounded-full shadow-md cursor-grab z-10 ${
            dragging === 'end' ? 'cursor-grabbing scale-110 z-20' : ''
          } hover:scale-110 transition-transform flex items-center justify-center`}
          style={{ left: `calc(${endPercent}% - 10px)` }}
          onPointerDown={handlePointerDown('end')}
        >
          <div className="w-1 h-3 bg-white rounded-full" />
        </div>

        {/* 再生位置インジケーター */}
        {playbackPercent !== null &&
          playbackPercent >= 0 &&
          playbackPercent <= 100 && (
            <div
              className="absolute top-0 w-0.5 h-full bg-red-500 z-30 pointer-events-none"
              style={{ left: `${playbackPercent}%` }}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full" />
            </div>
          )}

        {/* シナリオ実行マーカー */}
        {markers.map((marker, index) => {
          const markerPercent = getPercent(marker.time)
          if (markerPercent < 0 || markerPercent > 100) return null
          return (
            <div
              key={index}
              className="absolute top-0 h-full z-25 cursor-pointer group"
              style={{ left: `${markerPercent}%` }}
              onClick={(e) => {
                e.stopPropagation()
                onMarkerClick?.(marker.time)
              }}
              title={`${marker.label} - ${format(marker.time, 'HH:mm:ss')}`}
            >
              {/* マーカーライン */}
              <div
                className="w-0.5 h-full opacity-70 group-hover:opacity-100 transition-opacity"
                style={{ backgroundColor: marker.color || '#10b981' }}
              />
              {/* マーカートップ（三角形） */}
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[8px] border-l-transparent border-r-transparent group-hover:scale-125 transition-transform"
                style={{ borderTopColor: marker.color || '#10b981' }}
              />
              {/* ホバー時のラベル */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {marker.label}
              </div>
            </div>
          )
        })}
      </div>

      {/* 選択範囲の時刻表示 */}
      <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 text-sm">
        <span
          className={`font-medium whitespace-nowrap ${dragging ? 'text-primary-500' : 'text-primary-700'}`}
        >
          {formatTime(displayStart)}
        </span>
        <span className="text-gray-400">〜</span>
        <span
          className={`font-medium whitespace-nowrap ${dragging ? 'text-primary-500' : 'text-primary-700'}`}
        >
          {formatTime(displayEnd)}
        </span>
      </div>
    </div>
  )
}
